import { useEffect, useId, useRef, useState, type ReactNode, type TextareaHTMLAttributes } from 'react';
import { useFormContext } from 'react-hook-form';

import useIsPWA from '@/hooks/use-is-pwa';
import { errorMessage, type FieldErrorLike } from '@/types/form';

export interface TextareaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'value'> {
    name: string;
    label?: ReactNode;
    /** Valeur courante, pour le libellé flottant et le compteur de caractères. */
    value?: string | null;
    error?: FieldErrorLike;
}

/** Zone de texte à libellé flottant, enregistrée dans le formulaire react-hook-form parent. */
const Textarea = (props: TextareaProps) => {
    const { label, value, error, required, maxLength, name, ...rest } = props;
    const { register } = useFormContext();
    const registration = register(name);
    const id = useId();
    const errorId = `${id}-error`;
    const [focused, setFocused] = useState(false);
    const isPWA = useIsPWA();
    const labelRef = useRef<HTMLLabelElement>(null);
    const [isMultiLine, setIsMultiLine] = useState(false);

    const isActive = focused || value;

    useEffect(() => {
        if (isPWA && labelRef.current && isActive) {
            setIsMultiLine(labelRef.current.offsetHeight > 20);
        }
    }, [isPWA, label, isActive]);

    return (
        <div className="relative w-full">
            <textarea
                id={id}
                required={required}
                aria-required={required || undefined}
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? errorId : undefined}
                {...registration}
                className={`w-full px-6 ${isPWA && isMultiLine && isActive ? 'pt-6 pb-2' : 'py-4'} rounded-lg border border-border bg-transparent text-base transition-colors placeholder-transparent resize-y min-h-[100px] focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary ${error ? 'border-destructive focus:ring-destructive/20 focus:border-destructive' : ''}`}
                maxLength={maxLength}
                placeholder={typeof label === 'string' ? label : undefined}
                onFocus={() => setFocused(true)}
                onBlur={(e) => {
                    void registration.onBlur(e);
                    setFocused(false);
                }}
                {...rest}
            />
            <label
                ref={labelRef}
                htmlFor={id}
                className={`absolute left-3 ${isPWA ? (isActive ? 'bg-white' : 'bg-transparent') : 'bg-card'} px-1 text-muted-foreground transition-all duration-200 pointer-events-none ${isActive ? 'text-xs -top-2 text-primary' : 'text-sm top-3'} ${error && isActive ? 'text-destructive' : ''}`}
            >
                {label}
                {required ? (
                    <sup className="text-destructive ml-0.5" aria-label="obligatoire">
                        *
                    </sup>
                ) : (
                    ''
                )}
            </label>
            {maxLength && value && (
                <p className="text-xs text-muted-foreground text-right mt-1">
                    {maxLength - value.length} / {maxLength}
                </p>
            )}
            {error && (
                <p id={errorId} role="alert" aria-live="assertive" className="text-xs text-destructive italic mt-1">
                    {errorMessage(error)}
                </p>
            )}
        </div>
    );
};

export default Textarea;
